import { Navigate } from 'react-router';
import { useMemo, useState, useEffect } from 'react';

import { usePathname } from 'src/routes/hooks';

import { Modules, Permissions } from 'src/utils/constants';

import {
  useGetModules,
  useGetLoggedInUserModulePermissions,
} from 'src/api/services/module-permission/modulePermissionService';

import { toast } from 'src/components/snackbar';
import { SplashScreen } from 'src/components/loading-screen';

import { useAuthContext } from '../hooks';
import { PermissionsProvider } from '../context/permission-context';

// MODULE_PATHS, API'den gelen modül yollarıyla eşleştirilecek
const MODULE_PATHS = {
  [Modules.HOME]: '/',
  [Modules.SCHEDULE]: '/schedule',
  [Modules.DOCUMENTS]: '/documents',
  [Modules.ORGANIZATION]: '/organization',
  [Modules.COMPETITION]: '/competition',
  [Modules.PROJECT]: '/projects',
  [Modules.PARTICIPATION]: '/participation',
  [Modules.PARTICIPANT]: 'participation/participant',
  [Modules.INVITATION_LETTER]: 'participation/invitation-letter',
  [Modules.VIP]: 'participation/vip',
  [Modules.USER]: '/user',
  [Modules.PAYMENT]: '/payment',
};

// İzin tiplerinin adlarını ID'lere göre eşleştiren obje
const PERMISSION_NAMES = Object.entries(Permissions).reduce((acc, [key, value]) => {
  acc[value] = key;
  return acc;
}, {});

export function ModuleGuard({ children }) {
  const { user } = useAuthContext();
  const pathname = usePathname();
  const [isLoading, setIsLoading] = useState(true);

  // Kullanıcının görünür modüllerini al
  const visibleModules = user?.VisibleModuleIds || [];

  // API'den tüm modülleri al
  const { modules, modulesLoading } = useGetModules();

  // Mevcut URL'den modül ID'sini belirle
  const pathParts = pathname.split('/').filter(Boolean);

  // API'den gelen modül bilgilerini kullanarak modül ID'sini belirle
  const moduleId = useMemo(() => {
    if (!modules || modules.length === 0) return 1;

    // Özel durum: Dashboard ana sayfası için (URL: / veya '')
    if (pathname === '/' || pathname === '' || pathname.startsWith('/home')) {
      // Home modülünü bul
      const homeModule = modules.find(
        (module) => module.Path === '/' || module.Name.toLowerCase() === 'home'
      );
      if (homeModule) {
        return homeModule.Id;
      }
    }

    // Özel durum: pathname "define" veya "super-define" ile başlıyorsa
    const cleanPath = pathname.startsWith('/') ? pathname.substring(1) : pathname;

    if (cleanPath.startsWith('define') || cleanPath.startsWith('super-define')) {
      // pathname'in başladığı kelime ile eşleşen bir modül var mı diye kontrol et
      const specialModuleKeyword = cleanPath.startsWith('define') ? 'define' : 'super-define';
      const matchingModule = modules.find((module) => {
        const modulePath = module.Path || '';
        const normalizedModulePath = modulePath.startsWith('/')
          ? modulePath.substring(1)
          : modulePath;
        return (
          normalizedModulePath === specialModuleKeyword ||
          normalizedModulePath.startsWith(`${specialModuleKeyword}/`)
        );
      });

      if (matchingModule) {
        return matchingModule.Id;
      }
    }

    // Modülleri önce child modüller (ParentModuleId > 0) sonra parent modüller olacak şekilde sırala
    // Bu şekilde önce en spesifik modülleri kontrol edeceğiz
    const sortedModules = [...modules].sort((a, b) => {
      // Önce ParentModuleId'si olan (child) modülleri öne al
      if (a.ParentModuleId > 0 && b.ParentModuleId === 0) return -1;
      if (a.ParentModuleId === 0 && b.ParentModuleId > 0) return 1;
      return 0;
    });

    // Hiyerarşik yol eşleşmesi için fonksiyon
    const findBestModuleMatch = (currentPathParts) => {
      if (currentPathParts.length === 0) return null;

      // En iyi eşleşmeyi bulmak için değişkenler
      let bestMatch = null;
      let bestMatchLength = 0;
      let bestMatchDepth = 0; // Modül hiyerarşisindeki derinlik

      sortedModules.forEach((module) => {
        if (!module.Path) {
          return;
        }

        // Modül yolunu normalize et
        let modulePath = module.Path;
        if (!modulePath.startsWith('/')) {
          modulePath = `/${modulePath}`;
        }
        if (modulePath === '') modulePath = '/';
        const modulePathParts = modulePath.split('/').filter(Boolean);

        // Modül yolu ile mevcut URL'nin eşleşip eşleşmediğini kontrol et
        let isMatch = false;

        // Özel durum: Kök yol (/) için
        if (modulePath === '/' && currentPathParts.length === 0) {
          isMatch = true;
        } else if (currentPathParts.length >= modulePathParts.length) {
          // Tam yol eşleşmesi kontrolü
          // URL'nin sonunda modül yolu var mı kontrol et (child modüller için önemli)
          // URL'nin son kısmını kontrol et
          const lastPathParts = currentPathParts.slice(-modulePathParts.length);

          isMatch = modulePathParts.every((part, index) => lastPathParts[index] === part);
        }

        // Eğer eşleşme varsa ve daha iyi bir eşleşme ise, bu modülü seç
        if (isMatch) {
          // Modül derinliğini hesapla (child modüller için daha yüksek)
          const moduleDepth = module.ParentModuleId > 0 ? 1 : 0;

          // Daha uzun yol eşleşmesi veya aynı uzunlukta ama daha derin modül (child) ise güncelle
          if (
            modulePathParts.length > bestMatchLength ||
            (modulePathParts.length === bestMatchLength && moduleDepth > bestMatchDepth)
          ) {
            bestMatch = module.Id;
            bestMatchLength = modulePathParts.length;
            bestMatchDepth = moduleDepth;
          }
        }
      });

      return bestMatch;
    };

    // Hiyerarşik olarak yol eşleşmesi ara
    // Önce tam yolu dene, eşleşme yoksa bir üst yolu dene
    let moduleMatch = null;
    const currentPathParts = [...pathParts];

    // Tam yoldan başlayarak, her seferinde bir segment azaltarak eşleşme ara
    while (currentPathParts.length > 0 && moduleMatch === null) {
      moduleMatch = findBestModuleMatch(currentPathParts);

      // Eşleşme bulunamadıysa, son segmenti çıkar ve tekrar dene
      if (moduleMatch === null) {
        currentPathParts.pop();
      }
    }

    // Eğer hala bir eşleşme bulunamadıysa ve ana sayfadaysak, varsayılan olarak HOME modülünü kullan
    if (moduleMatch === null && (pathParts.length === 0 || pathname === '/' || pathname === '')) {
      const defaultHomeModule = modules.find((m) => m.Id === Modules.HOME);
      if (defaultHomeModule) {
        return defaultHomeModule.Id;
      }
    }
    if (currentPathParts.length === 0) {
      console.log('currentPathParts is not found');
    } else {
      // console.log('Found module match:', moduleMatch, 'for path parts:', currentPathParts);
    }
    return moduleMatch;
  }, [modules, pathParts, pathname]);

  // console.log('moduleId', moduleId);

  if (moduleId === null) {
    console.log('moduleId is not found');
  }

  // Modül ID'sine göre izinleri al
  const { modulePermissions, modulePermissionsLoading } =
    useGetLoggedInUserModulePermissions(moduleId);

  useEffect(() => {
    if (!modulesLoading && !modulePermissionsLoading) {
      setIsLoading(false);
    }
  }, [modulesLoading, modulePermissionsLoading]);

  // İzin kontrolü için gerekli değerleri hazırla
  const permissionValue = useMemo(
    () => ({
      permissions: modulePermissions,
      moduleId,
      isLoading: modulePermissionsLoading || modulesLoading,
      hasPermission: (permissionType) => {
        // Eğer permissionType bir string ise, karşılık gelen ID'yi bul
        const permissionId =
          typeof permissionType === 'string' ? Permissions[permissionType] : permissionType;

        return modulePermissions?.some((p) => p.PermissionId === permissionId);
      },
      hasSpecialPermission: (permissionId) => modulePermissions?.some((p) => p.Id === permissionId),
    }),
    [moduleId, modulePermissions, modulePermissionsLoading, modulesLoading]
  );

  // console.log('user',user);

  //         if (moduleId && modules && modulePermissions) {
  //             const currentModule = modules.find(m => m.Id === moduleId);

  //             console.log(`%c📋 Modül İzinleri: ${currentModule?.Name || 'Bilinmeyen Modül'} (ID: ${moduleId})`, 'color: #4CAF50; font-weight: bold;');

  //             console.log('%cMevcut URL:', 'font-weight: bold;', pathname);

  //             console.log('%cKullanıcı İzinleri:', 'font-weight: bold;');
  //             console.log('%cKullanıcı Rolü:', 'font-weight: bold;', user?.RoleName);

  //             if (modulePermissions.length === 0) {
  //                 console.log('%c⚠️ Bu modül için hiç izin bulunamadı!', 'color: #FFC107;');
  //             } else {
  //                 const formattedPermissions = modulePermissions.map(permission => ({
  //                     Id: permission.Id,
  //                     PermissionId: permission.PermissionId,
  //                     PermissionName: PERMISSION_NAMES[permission.PermissionId] || 'Bilinmeyen İzin',
  //                     Description: permission.Description
  //                 }));

  //                 console.table(formattedPermissions);

  //             }
  //         }

  if (isLoading) {
    return <SplashScreen />;
  }

  // Kullanıcının bu modüle erişim izni var mı kontrol et
  if (visibleModules && moduleId && !visibleModules.includes(moduleId)) {
    toast.error('You are not authorized on this module!');
    return <Navigate to="/" replace />;
  }

  return <PermissionsProvider value={permissionValue}>{children}</PermissionsProvider>;
}
